/**
 * ActivationChecklist — ONE data-driven component, three variants (contracts
 * §4, DC-15, AC-040):
 *   full  = page body (header with intent goal line, progress, domain gate
 *           panel, grouped rows, advisories, complete-collapse)
 *   short = Sidebar.footer card  (body in ActivationShortForm — Plan D fills in)
 *   badge = nav chip + popover   (body in ActivationBadge — Plan D fills in)
 *
 * Router-free (DC-9): `projectId` is a prop, navigation goes through
 * `onNavigate`, dialogs through `openDialog` — no react-router import, no
 * direct location reads anywhere in this module.
 *
 * Copy rule D4: never print funnelId/versionId/webPlacementId/preflight —
 * every string here is either a static English literal owned by this
 * component (group titles, goal labels, chrome copy) or comes verbatim from
 * the server projection (`item.title`/`detail`/`action.label`,
 * `advisory.title`/`detail`), which already excludes internal ids (AM-4).
 */
import React, { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { ActivationAction, ActivationItem } from '@modules/shared-types';
import { questionnaireLink, runActivationAction } from '../actions/runActivationAction';
import type { ActivationActionContext } from '../actions/runActivationAction';
import { useLaunchActivation } from '../hooks/useLaunchActivation';
import { LAUNCH_ACTIVATION_TEST_IDS as T } from '../testIds';
import { GOAL_LABELS } from '../copy/intentCopy';
import { ActivationBadgeBody } from './ActivationBadge';
import { ActivationItemRow } from './ActivationItemRow';
import { ActivationProgress } from './ActivationProgress';
import { ActivationShortForm } from './ActivationShortForm';
import { IntentPopover } from './IntentPopover';
import { DomainGateSlot } from './DomainGateSlot';

export interface ActivationSurfaceProps {
  projectId: string;
  variant: 'full' | 'short' | 'badge';
  onNavigate: (path: string) => void;
  /** short: sidebar collapsed → chip (Plan D reads this in the body). */
  collapsed?: boolean;
  openDialog?: (dialog: NonNullable<ActivationAction['dialog']>, props?: Record<string, string>) => boolean | void;
  /** Published intent questionnaire (VITE_LAUNCH_INTENT_FUNNEL_URL). */
  questionnaireUrl?: string;
  /** Additive: a hook result supplied by the host (the page) so the surface does not fetch twice. */
  activation?: ReturnType<typeof useLaunchActivation>;
}

const GROUP_TITLES: Record<ActivationItem['group'], string> = {
  core: 'Path to launch',
  money: 'To sell',
  ads: 'To run ads',
};

const CONTENT_MAX_WIDTH = 980;

/** M-c: true when the advisory's detail already opens with its title, making the bold title a duplicate noun. */
const titleIsRedundant = (advisory: { title: string; detail: string }): boolean =>
  advisory.detail.trim().toLowerCase().startsWith(advisory.title.trim().toLowerCase());

export const ActivationChecklist: React.FC<ActivationSurfaceProps> = ({ projectId, variant, onNavigate, collapsed, openDialog, questionnaireUrl, activation }) => {
  const own = useLaunchActivation(activation ? undefined : projectId);
  const { data, loading, error, refetch, setIntent } = activation ?? own;
  const [expanded, setExpanded] = useState(false);
  const [intentAnchor, setIntentAnchor] = useState<HTMLElement | null>(null);
  const refresh = (): void => {
    void refetch();
  };

  const act = (item: ActivationItem): void => {
    if (!item.action) return;
    // `openDialog` is optional on the surface prop (no host mounted → the
    // fallbackRoute path); `runActivationAction` treats a non-function value
    // as "no dialog host" and falls back, so pass it through as-is rather
    // than substituting a no-op (which would swallow the fallback).
    runActivationAction(item.action, { onNavigate, openDialog: openDialog as ActivationActionContext['openDialog'] });
  };

  const domainItem = useMemo(() => data?.items.find((i) => i.id === 'domainVerified'), [data]);
  const rows = useMemo(() => (data?.items ?? []).filter((i) => i.id !== 'domainVerified'), [data]);
  const firstOpenId = rows.find((i) => !i.earned)?.id;

  if (variant !== 'full') {
    if (!data) return null;
    const body = { projection: data, onNavigate, openDialog, collapsed, onRefresh: refresh, questionnaireUrl };
    return variant === 'short' ? <ActivationShortForm {...body} /> : <ActivationBadgeBody {...body} />;
  }

  return (
    <Box data-testid={T.page} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, maxWidth: CONTENT_MAX_WIDTH }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" component="h1" gutterBottom>Launch</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <span data-testid={T.intentGoal}>Goal: {GOAL_LABELS[data?.intent.goal ?? 'first_value']}</span>
            {' · '}
            <Link
              data-testid={T.intentChange}
              component="button"
              type="button"
              underline="hover"
              aria-haspopup="true"
              aria-expanded={Boolean(intentAnchor)}
              onClick={(event: React.MouseEvent<HTMLElement>) => setIntentAnchor(event.currentTarget)}
              sx={{ fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit', verticalAlign: 'baseline' }}
            >
              change
            </Link>
          </Typography>
          {data && (
            <IntentPopover
              anchorEl={intentAnchor}
              open={Boolean(intentAnchor)}
              onClose={() => setIntentAnchor(null)}
              intent={data.intent}
              questionnaireHref={questionnaireUrl ? questionnaireLink(questionnaireUrl, projectId) : undefined}
              onApply={setIntent}
            />
          )}
        </Box>
        <IconButton onClick={refresh} disabled={loading} aria-label="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && !data && !loading && (
        <Alert
          severity="error"
          sx={{ maxWidth: CONTENT_MAX_WIDTH }}
          action={<Button data-testid={T.retry} color="inherit" size="small" onClick={refresh}>Retry</Button>}
        >
          Could not load the checklist: {error}
        </Alert>
      )}

      {loading && !data && (
        <Box sx={{ maxWidth: CONTENT_MAX_WIDTH }}>
          <Skeleton variant="rounded" height={8} sx={{ mb: 2 }} />
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="text" height={48} />)}
        </Box>
      )}

      {data && (
        <Box sx={{ maxWidth: CONTENT_MAX_WIDTH }}>
          {error && <Alert severity="warning" sx={{ mb: 2 }}>Could not refresh: {error}</Alert>}
          <ActivationProgress projection={data} />

          {data.complete && !expanded ? (
            <Paper data-testid={T.complete} sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1">Everything is ready to launch · {data.earnedCount} of {data.totalCount}</Typography>
              <Button data-testid={T.completeToggle} size="small" onClick={() => setExpanded(true)}>Full list</Button>
            </Paper>
          ) : (
            <>
              {domainItem && <DomainGateSlot item={domainItem} onAction={act} onRetry={refresh} />}
              <Paper data-testid={T.list}>
                {rows.map((item, index) => (
                  <React.Fragment key={item.id}>
                    {(index === 0 || rows[index - 1].group !== item.group) && (
                      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', px: 2.5, pt: 1.5 }}>
                        {GROUP_TITLES[item.group]}
                      </Typography>
                    )}
                    <ActivationItemRow item={item} primary={item.id === firstOpenId} onAction={act} onRetry={refresh} />
                  </React.Fragment>
                ))}
              </Paper>
            </>
          )}

          {data.advisories.map((advisory) => (
            <Typography key={advisory.id} data-testid={T.advisory(advisory.id)} variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {/* M-c: some AM-4 details already open with the advisory's own noun ("Product analytics:
                  Mixpanel — …"). Rendering the title too reads as "Product analytics. Product analytics: …",
                  so the title is dropped for those rows — the AM-4 strings themselves stay verbatim. */}
              {titleIsRedundant(advisory) ? advisory.detail : <><strong>{advisory.title}.</strong> {advisory.detail}</>}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export const ActivationBadge: React.FC<Omit<ActivationSurfaceProps, 'variant'>> = (props) => (
  <ActivationChecklist {...props} variant="badge" />
);
